import { useState, useEffect } from 'react';
import { useBatch } from '@/contexts/BatchContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Clock, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

interface TestSettings {
  lockHoursBeforeStart: number;
  defaultTestDuration: number;
  maxTestDuration: number;
  minTestDuration: number;
  allowLateSubmissions: boolean;
  autoPublishScheduledTests: boolean;
}

const TestSettingsPage = () => {
  useBatch(); // We need the context but don't use currentBatch directly
  const { toast } = useToast();

  const [settings, setSettings] = useState<TestSettings>({
    lockHoursBeforeStart: 24,
    defaultTestDuration: 60,
    maxTestDuration: 300,
    minTestDuration: 5,
    allowLateSubmissions: false,
    autoPublishScheduledTests: true
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // TODO: Implement API call to load tenant settings
      // const response = await settingsApi.getTestSettings();
      // setSettings(response.data);

      // For now, use default values
      console.log('Loading test settings...');
    } catch (error) {
      console.error('Failed to load test settings:', error);
      toast({
        title: "Error",
        description: "Failed to load test settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      // TODO: Implement API call to save tenant settings
      // await settingsApi.updateTestSettings(settings);

      console.log('Saving test settings:', settings);
      toast({
        title: "Success",
        description: "Test settings saved successfully",
      });
    } catch (error) {
      console.error('Failed to save test settings:', error);
      toast({
        title: "Error",
        description: "Failed to save test settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: keyof TestSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/settings">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Test Settings</h1>
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/settings">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Test Settings</h1>
          <p className="text-muted-foreground">Configure test-related settings for your institution</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Lock Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Test Lock Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lockHours">Lock Hours Before Start</Label>
              <Select
                value={settings.lockHoursBeforeStart.toString()}
                onValueChange={(value) => handleSettingChange('lockHoursBeforeStart', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="6">6 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours (recommended)</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">72 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Tests will be locked this many hours before their scheduled start time to prevent modifications.
              </p>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Important</p>
                  <p className="text-amber-700">
                    This setting applies to all new tests created in your institution.
                    Individual tests can override this setting during creation.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Duration Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Duration Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="defaultDuration">Default Test Duration (minutes)</Label>
              <Input
                id="defaultDuration"
                type="number"
                min="5"
                max="300"
                value={settings.defaultTestDuration}
                onChange={(e) => handleSettingChange('defaultTestDuration', parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="minDuration">Minimum Duration (minutes)</Label>
              <Input
                id="minDuration"
                type="number"
                min="1"
                max="60"
                value={settings.minTestDuration}
                onChange={(e) => handleSettingChange('minTestDuration', parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxDuration">Maximum Duration (minutes)</Label>
              <Input
                id="maxDuration"
                type="number"
                min="60"
                max="600"
                value={settings.maxTestDuration}
                onChange={(e) => handleSettingChange('maxTestDuration', parseInt(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveSettings} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default TestSettingsPage;
